/**
 * Civic geography for the Brazil figure. Names, IBGE capitals and regions are
 * public facts. Colour on the pre-session map is the watched-SEFAZ allow-list
 * plus a four-tone BackChannel green so neighbours stay distinct. Never a
 * published rate.
 */
export const BRAZIL_UF_IDS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
] as const;

export type BrazilUf = (typeof BRAZIL_UF_IDS)[number];
export type BrazilRegion = "north" | "northeast" | "centralWest" | "southeast" | "south";

const UF_SET = new Set<string>(BRAZIL_UF_IDS);

export function isBrazilUf(value: string): value is BrazilUf {
  return UF_SET.has(value.toUpperCase());
}

export const BRAZIL_UF_NAMES: Record<BrazilUf, string> = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

export const BRAZIL_UF_REGIONS: Record<BrazilUf, BrazilRegion> = {
  AC: "north",
  AL: "northeast",
  AP: "north",
  AM: "north",
  BA: "northeast",
  CE: "northeast",
  DF: "centralWest",
  ES: "southeast",
  GO: "centralWest",
  MA: "northeast",
  MT: "centralWest",
  MS: "centralWest",
  MG: "southeast",
  PA: "north",
  PB: "northeast",
  PR: "south",
  PE: "northeast",
  PI: "northeast",
  RJ: "southeast",
  RN: "northeast",
  RS: "south",
  RO: "north",
  RR: "north",
  SC: "south",
  SP: "southeast",
  SE: "northeast",
  TO: "north",
};

/** IBGE capitals. Not a tax table. */
export const BRAZIL_UF_CAPITALS: Record<BrazilUf, string> = {
  AC: "Rio Branco",
  AL: "Maceió",
  AP: "Macapá",
  AM: "Manaus",
  BA: "Salvador",
  CE: "Fortaleza",
  DF: "Brasília",
  ES: "Vitória",
  GO: "Goiânia",
  MA: "São Luís",
  MT: "Cuiabá",
  MS: "Campo Grande",
  MG: "Belo Horizonte",
  PA: "Belém",
  PB: "João Pessoa",
  PR: "Curitiba",
  PE: "Recife",
  PI: "Teresina",
  RJ: "Rio de Janeiro",
  RN: "Natal",
  RS: "Porto Alegre",
  RO: "Porto Velho",
  RR: "Boa Vista",
  SC: "Florianópolis",
  SP: "São Paulo",
  SE: "Aracaju",
  TO: "Palmas",
};

/**
 * Undirected land borders. Used only so neighbouring UFs get different
 * BackChannel greens on the login map — not a tax table.
 */
const UF_BORDER_PAIRS: ReadonlyArray<readonly [BrazilUf, BrazilUf]> = [
  ["AC", "AM"],
  ["AC", "RO"],
  ["AL", "BA"],
  ["AL", "PE"],
  ["AL", "SE"],
  ["AM", "MT"],
  ["AM", "PA"],
  ["AM", "RO"],
  ["AM", "RR"],
  ["AP", "PA"],
  ["BA", "ES"],
  ["BA", "GO"],
  ["BA", "MG"],
  ["BA", "PE"],
  ["BA", "PI"],
  ["BA", "SE"],
  ["BA", "TO"],
  ["CE", "PB"],
  ["CE", "PE"],
  ["CE", "PI"],
  ["CE", "RN"],
  ["DF", "GO"],
  ["ES", "MG"],
  ["ES", "RJ"],
  ["GO", "MG"],
  ["GO", "MS"],
  ["GO", "MT"],
  ["GO", "TO"],
  ["MA", "PA"],
  ["MA", "PI"],
  ["MA", "TO"],
  ["MG", "MS"],
  ["MG", "RJ"],
  ["MG", "SP"],
  ["MS", "MT"],
  ["MS", "PR"],
  ["MS", "SP"],
  ["MT", "PA"],
  ["MT", "RO"],
  ["MT", "TO"],
  ["PA", "RR"],
  ["PA", "TO"],
  ["PB", "PE"],
  ["PB", "RN"],
  ["PE", "PI"],
  ["PI", "TO"],
  ["PR", "SC"],
  ["PR", "SP"],
  ["RJ", "SP"],
  ["RS", "SC"],
];

function neighborsByUf(): Record<BrazilUf, readonly BrazilUf[]> {
  const neighbors = Object.fromEntries(BRAZIL_UF_IDS.map((uf) => [uf, [] as BrazilUf[]])) as Record<
    BrazilUf,
    BrazilUf[]
  >;
  for (const [left, right] of UF_BORDER_PAIRS) {
    neighbors[left].push(right);
    neighbors[right].push(left);
  }
  return neighbors;
}

export const BRAZIL_UF_NEIGHBORS = neighborsByUf();

export const BRAZIL_UF_TONE_IDS = ["0", "1", "2", "3"] as const;
export type BrazilUfTone = (typeof BRAZIL_UF_TONE_IDS)[number];

/**
 * Four BackChannel greens so adjacent states stay distinct while they paint.
 * Watched vs unwatched is still the only fiscal meaning.
 */
function toneByUf(): Record<BrazilUf, BrazilUfTone> {
  const order = [...BRAZIL_UF_IDS].sort(
    (left, right) =>
      BRAZIL_UF_NEIGHBORS[right].length - BRAZIL_UF_NEIGHBORS[left].length ||
      left.localeCompare(right),
  );
  const tones = {} as Record<BrazilUf, BrazilUfTone>;
  for (const uf of order) {
    const used = new Set(
      BRAZIL_UF_NEIGHBORS[uf]
        .map((neighbor) => tones[neighbor])
        .filter((tone): tone is BrazilUfTone => tone !== undefined),
    );
    const tone = BRAZIL_UF_TONE_IDS.find((candidate) => !used.has(candidate));
    tones[uf] = tone ?? "0";
  }
  return tones;
}

export const BRAZIL_UF_TONES = toneByUf();

export function brazilUfTone(uf: string): BrazilUfTone {
  const code = uf.toUpperCase();
  return isBrazilUf(code) ? BRAZIL_UF_TONES[code] : "0";
}

/** IBGE macro-region codes, shown on the regional card art tile. */
export const BRAZIL_REGION_CODES: Record<BrazilRegion, string> = {
  north: "N",
  northeast: "NE",
  centralWest: "CO",
  southeast: "SE",
  south: "S",
};

/**
 * Published commercial office from backchannel.com.br / careers.
 * This is the Vila Olímpia desk, not a retail store and not the Receita
 * registered address (Avenida Paulista — see `BACKCHANNEL_LEGAL_ENTITY`).
 * `mapPoint` is São Paulo city (23.55°S, 46.63°W) inside the SP path of
 * `@svg-maps/brazil` (viewBox 0 0 613 639), interpolated from the SP bounds.
 */
export const BACKCHANNEL_SAO_PAULO_OFFICE = {
  uf: "SP",
  addressLine: "Rua Tenerife 31, Conj. 31A",
  neighborhood: "Vila Olímpia",
  city: "São Paulo",
  postalCode: "04548-040",
  phone: "+55 11 4040-7721",
  mapsQuery: "Rua Tenerife 31, Conj. 31A, Vila Olímpia, São Paulo - SP",
  mapPoint: { x: 428, y: 458 },
} as const;

export function brazilUfName(uf: string): string {
  const code = uf.toUpperCase();
  return isBrazilUf(code) ? BRAZIL_UF_NAMES[code] : uf;
}

export function brazilUfRegion(uf: string): BrazilRegion | undefined {
  const code = uf.toUpperCase();
  return isBrazilUf(code) ? BRAZIL_UF_REGIONS[code] : undefined;
}

export function brazilUfCapital(uf: string): string | undefined {
  const code = uf.toUpperCase();
  return isBrazilUf(code) ? BRAZIL_UF_CAPITALS[code] : undefined;
}

/** Reads `BR-SP` or `SP`. `BR-Federal` is not a UF and must not become AL. */
export function ufFromJurisdiction(value: string | null | undefined): BrazilUf | null {
  if (!value) return null;
  const trimmed = value.trim().toUpperCase();
  if (isBrazilUf(trimmed)) return trimmed;
  const match = /^BR-([A-Z]{2})$/.exec(trimmed);
  const code = match?.[1];
  return code && isBrazilUf(code) ? code : null;
}

export function backchannelOfficeAddress(): string {
  const office = BACKCHANNEL_SAO_PAULO_OFFICE;
  return `${office.addressLine}, ${office.neighborhood}, ${office.city} - ${office.uf}, ${office.postalCode}`;
}

export function backchannelOfficeMapsUrl(): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    BACKCHANNEL_SAO_PAULO_OFFICE.mapsQuery,
  )}`;
}
